import React, { useCallback, useEffect, useRef, useState } from "react";
import { SvgComponent } from "@commons/Componentes/SvgComponent/SvgComponent";
import { ampliarConocimiento, buscarAmpliacion } from "../../Services/conocimientoIaService";

const POR_MODULO = "";

export const RevisionAmpliacion = ({ idSec, documentos, moduloRespuesta, maxLargo, onMensaje, onFallo, onAmpliado }) => {
  const [documentoBase, setDocumentoBase] = useState(documentos[0]?.Nombre ?? POR_MODULO);
  const [ampliacion, setAmpliacion] = useState(null);
  const [buscando, setBuscando] = useState(false);
  const [contenido, setContenido] = useState("");
  const [publicando, setPublicando] = useState(false);

  const consultaActual = useRef(0);

  // useMessage entrega funciones nuevas en cada render: depender de onFallo dejaría la búsqueda en bucle.
  const falloRef = useRef(onFallo);
  falloRef.current = onFallo;

  const cargar = useCallback(async (documento) => {
    const id = ++consultaActual.current;
    setBuscando(true);
    setAmpliacion(null);
    try {
      const datos = await buscarAmpliacion(idSec, documento);
      if (id === consultaActual.current) setAmpliacion(datos);
    } catch (error) {
      if (id === consultaActual.current) falloRef.current(error);
    } finally {
      if (id === consultaActual.current) setBuscando(false);
    }
  }, [idSec]);

  useEffect(() => {
    cargar(documentoBase);
  }, [cargar, documentoBase]);

  const publicar = async () => {
    const texto = contenido.trim();
    if (!texto || publicando || !ampliacion) return;

    setPublicando(true);
    try {
      const datos = await ampliarConocimiento(idSec, documentoBase, texto, ampliacion.Version);
      setAmpliacion(datos?.Ampliacion || ampliacion);
      setContenido("");
      if (onAmpliado) onAmpliado(datos?.Revision);
      if (datos?.Mensaje) onMensaje(datos.Mensaje);
    } catch (error) {
      onFallo(error);
    } finally {
      setPublicando(false);
    }
  };

  const ocupado = buscando || publicando;

  return (
    <div className="citrus-ia-ampliacion">
      <div className="citrus-ia-fila">
        <div className="citrus-ia-campo">
          <label htmlFor="ia-ampliar-documento" title="El documento al que se le agrega el conocimiento">
            Documento a ampliar
          </label>
          <select
            id="ia-ampliar-documento"
            value={documentoBase}
            onChange={(e) => setDocumentoBase(e.target.value)}
            disabled={ocupado}
          >
            {documentos.map((documento) => (
              <option key={documento.Clave} value={documento.Nombre}>
                {documento.Nombre}
              </option>
            ))}
            <option value={POR_MODULO}>
              {moduloRespuesta ? `Por módulo: ${moduloRespuesta}` : "General, sin documento"}
            </option>
          </select>
        </div>

        <div className="citrus-ia-campo">
          <span className="citrus-ia-campo__etiqueta">Archivo de ampliación</span>
          <div className="citrus-ia-ampliacion__archivo">
            {buscando || !ampliacion ? (
              <span className="citrus-ia-campo__nota">Buscando en la IA...</span>
            ) : (
              <>
                <span className="citrus-ia-ampliacion__nombre">{ampliacion.Nombre}</span>
                <span className={`citrus-gpt-tag citrus-gpt-tag--${ampliacion.Existe ? "modelo" : "neutra"}`}>
                  {ampliacion.Existe ? `Existe · versión ${ampliacion.Version}` : "Se creará nuevo"}
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      {ampliacion?.Existe && ampliacion.Contenido && (
        <details className="citrus-ia-ampliacion__actual">
          <summary>Ver lo que ya tiene el archivo</summary>
          <pre>{ampliacion.Contenido}</pre>
        </details>
      )}

      <div className="citrus-ia-campo">
        <label htmlFor="ia-ampliar-contenido" title="Se agrega al final del archivo, con su pregunta de origen como título">
          Nuevo conocimiento
        </label>
        <textarea
          id="ia-ampliar-contenido"
          className="citrus-ia-ampliacion__texto"
          rows={14}
          maxLength={maxLargo}
          placeholder="Explique lo que la IA debe saber, en Markdown simple (##, **negrita**, listas con -)."
          value={contenido}
          onChange={(e) => setContenido(e.target.value)}
          disabled={publicando}
        />
        <span className="citrus-ia-campo__nota">{contenido.length} / {maxLargo}</span>
      </div>

      <div className="citrus-ia-seccion__pie">
        <span className="citrus-ia-campo__nota">
          Se publica en el vector store y reemplaza la versión anterior del archivo. Puede tardar unos segundos.
        </span>
        <button
          type="button"
          className="citrus-ia-accion citrus-ia-accion--naranja"
          onClick={publicar}
          disabled={ocupado || !ampliacion || contenido.trim().length === 0}
        >
          <SvgComponent iconName="lightbulb" width="18px" height="18px" fill="currentColor" />
          <span>{publicando ? "Publicando..." : "Agregar a la IA"}</span>
        </button>
      </div>
    </div>
  );
};
